import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const LAST_UPDATED_ISO = "2026-07-23";
const LAST_UPDATED_LABEL = "23 July 2026";

const Section = ({ title, children }) => (
  <section className="mt-6" data-testid={`terms-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <h2 className="text-base font-semibold text-white">{title}</h2>
    <div className="mt-2 text-sm text-zinc-300 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div
      className="min-h-screen bg-[#0b0b0c] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="terms-screen"
    >
      <SEO
        title="Terms of Service"
        description="FreeFitFuel Terms of Service — subscription pricing, payment via Stripe, cancellation, refunds and acceptable use for the FreeFitFuel web app."
        path="/terms"
      />
      <Helmet>
        <meta name="revised" content={LAST_UPDATED_ISO} />
        <meta name="last-modified" content={LAST_UPDATED_ISO} />
      </Helmet>
      <div className="mx-auto max-w-2xl px-5 py-6 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="h-9 w-9 rounded-full grid place-items-center bg-zinc-900 hover:bg-zinc-800" data-testid="terms-back-home">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-xl font-semibold tracking-tight">Terms of Service</h1>
        </div>

        <div
          data-testid="terms-last-updated"
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#BEFF38]/30 bg-[#BEFF38]/10 px-3 py-1.5"
        >
          <span className="text-[10px] uppercase tracking-[0.16em] font-black text-[#BEFF38]">Last updated</span>
          <time dateTime={LAST_UPDATED_ISO} className="text-xs font-semibold text-white">{LAST_UPDATED_LABEL}</time>
        </div>

        <Section title="Agreement">
          <p>By using FreeFitFuel (freefitfuel.com and freefitfuel.app) you agree to these Terms. If you don't agree, please stop using the service.</p>
          <p>FreeFitFuel is currently delivered as a mobile-friendly web application. A native iOS app may be added in future; if that happens, additional App Store terms will apply and be posted here.</p>
        </Section>

        <Section title="What FreeFitFuel is, and isn't">
          <p>FreeFitFuel is a general fitness, nutrition and wellbeing companion. The training, nutrition, and recovery suggestions it generates are informational, not medical advice. They are not a substitute for guidance from a qualified doctor, physiotherapist, or registered dietitian.</p>
          <p>Always check with a healthcare professional before starting a new exercise programme, especially if you have an existing injury, condition, or are pregnant. Stop and seek medical help if you experience pain, dizziness, or other warning signs.</p>
        </Section>

        <Section title="Subscriptions & pricing">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>FreeFitFuel Pro Monthly</strong> — £5.00 per month, auto-renewing.</li>
            <li><strong>FreeFitFuel Pro Annual</strong> — £25.00 per year, auto-renewing.</li>
            <li><strong>Founder Annual</strong> — £19.00 per year, auto-renewing, price locked for life (available while founder places remain).</li>
            <li>Prices are in GBP and include applicable VAT.</li>
            <li>All prices are shown on the <Link to="/" className="text-lime-400 underline">homepage</Link> at time of purchase. If prices change, existing subscribers keep their current rate until their next renewal.</li>
          </ul>
        </Section>

        <Section title="Payment, renewal & cancellation">
          <ul className="list-disc pl-5 space-y-1">
            <li>Payments are processed by <strong>Stripe</strong>. We do not store your card number — Stripe holds it securely under PCI-DSS.</li>
            <li>Your subscription renews automatically at the end of each billing period. You are charged on the day of renewal.</li>
            <li>You can cancel at any time from Settings → Manage subscription, or by emailing <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a>. Cancellation stops future renewals; access continues until the end of the current paid period.</li>
            <li>If you lose access after paying (new device, cleared browser data, etc.), use Settings → "Already paid? Recover by email" to restore your Pro entitlement.</li>
          </ul>
        </Section>

        <Section title="Refunds">
          <p>We handle refunds directly. If you're within 14 days of a first purchase and haven't used the service materially, email <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a> and we'll refund you in full via Stripe. Beyond that we consider refunds on a case-by-case basis. Nothing here removes any statutory consumer rights you have under UK or EU law.</p>
        </Section>

        <Section title="Referral programme">
          <p>Users can share a personal referral code that grants a friend 30 days of Pro on first redeem, and adds 60 days to the referrer's own subscription. Codes can only be redeemed once per device, cannot be redeemed on your own account, and have no cash value.</p>
        </Section>

        <Section title="Acceptable use">
          <p>You agree to use FreeFitFuel for your own personal training and wellbeing. Don't attempt to reverse-engineer the service, abuse the AI Coach to generate harmful content, share your subscription with people who haven't paid, or upload illegal or non-consensual images to Food Snap.</p>
        </Section>

        <Section title="AI-generated content">
          <p>Workouts, meal plans, recovery sessions, coach replies and meal images are generated by large language and image models. Output can occasionally be inaccurate. Treat AI advice as a starting point — apply common sense and your knowledge of your own body.</p>
        </Section>

        <Section title="Your content">
          <p>You retain ownership of plan inputs, lift logs, food photos, and chat messages you create. You grant us a non-exclusive licence to process this content in order to deliver the service. We will not sell, license, or share it with third parties beyond the processors listed in our <Link to="/privacy" className="text-lime-400 underline">Privacy Policy</Link>.</p>
        </Section>

        <Section title="Intellectual property">
          <p>The FreeFitFuel name, logo, exercise library, prompts, curriculum content and interface are owned by us and protected by copyright and trademark law. You may not copy or redistribute them without written permission.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>FreeFitFuel is provided "as is". To the maximum extent permitted by law, we are not liable for indirect or consequential losses, missed training results, injuries arising from following AI-generated guidance, or for any temporary unavailability of the service.</p>
        </Section>

        <Section title="Termination">
          <p>You can stop using the service and cancel your subscription at any time. We can suspend or terminate access if these Terms are breached.</p>
        </Section>

        <Section title="Changes to these Terms">
          <p>We may update these Terms occasionally. Material changes will be highlighted in the app before they take effect.</p>
        </Section>

        <Section title="Governing law">
          <p>These Terms are governed by the laws of England and Wales. Disputes will be handled by the courts of England and Wales.</p>
        </Section>

        <Section title="Contact">
          <p>Questions: <a href="mailto:gcanth@gmail.com" className="text-lime-400 underline">gcanth@gmail.com</a></p>
        </Section>
      </div>
    </div>
  );
}
